import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

type WorkItem<T> = () => Promise<T>;

export class WorkerQueueCancelledError extends Error {
  constructor() {
    super('The queued work item was cancelled.');
    this.name = 'WorkerQueueCancelledError';
  }
}

export const isWorkerQueueCancelledError = (
  error: unknown
): error is WorkerQueueCancelledError =>
  error instanceof WorkerQueueCancelledError;

interface WorkerQueueContextProps {
  current: number;
  total: number;
  enqueue: <T>(work: WorkItem<T>) => Promise<T>;
  cancel: () => void;
}

interface QueueItem {
  cancelled: boolean;
}

const WorkerQueueContext = createContext<WorkerQueueContextProps | undefined>(
  undefined
);

export const WorkerQueueProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const queue = useRef<Promise<void>>(Promise.resolve());
  const pendingCount = useRef(0);
  const currentItem = useRef(0);
  const totalItems = useRef(0);
  const waitingItems = useRef(new Set<QueueItem>());
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  const resetProgress = useCallback(() => {
    currentItem.current = 0;
    totalItems.current = 0;
    setProgress({ current: 0, total: 0 });
  }, []);

  const finishWorkItem = useCallback(
    (item: QueueItem) => {
      if (item.cancelled) {
        return;
      }

      pendingCount.current -= 1;

      if (pendingCount.current > 0) {
        return;
      }

      resetProgress();
    },
    [resetProgress]
  );

  const cancel = useCallback(() => {
    const cancelledItemCount = waitingItems.current.size;

    if (cancelledItemCount === 0) {
      return;
    }

    waitingItems.current.forEach((item) => {
      item.cancelled = true;
    });
    waitingItems.current.clear();
    pendingCount.current -= cancelledItemCount;
    totalItems.current = currentItem.current;

    if (pendingCount.current === 0) {
      resetProgress();
      return;
    }

    setProgress({
      current: currentItem.current,
      total: totalItems.current,
    });
  }, [resetProgress]);

  const enqueue = useCallback(
    <T,>(work: WorkItem<T>): Promise<T> => {
      const item = { cancelled: false };
      waitingItems.current.add(item);
      pendingCount.current += 1;
      totalItems.current += 1;

      setProgress({
        current: currentItem.current,
        total: totalItems.current,
      });

      const result = queue.current.then(async () => {
        waitingItems.current.delete(item);

        if (item.cancelled) {
          throw new WorkerQueueCancelledError();
        }

        currentItem.current += 1;
        setProgress({
          current: currentItem.current,
          total: totalItems.current,
        });

        return work();
      });

      // Keep the queue running after a rejected work item.
      queue.current = result.then(
        () => undefined,
        () => undefined
      );

      void result.then(
        () => finishWorkItem(item),
        () => finishWorkItem(item)
      );

      return result;
    },
    [finishWorkItem]
  );

  const value = useMemo(
    () => ({
      current: progress.current,
      total: progress.total,
      enqueue,
      cancel,
    }),
    [cancel, enqueue, progress]
  );

  return (
    <WorkerQueueContext.Provider value={value}>
      {children}
    </WorkerQueueContext.Provider>
  );
};

export const useWorkerQueue = (): WorkerQueueContextProps => {
  const context = useContext(WorkerQueueContext);

  if (!context) {
    throw new Error('useWorkerQueue must be used within a WorkerQueueProvider');
  }

  return context;
};
